import { registerRemotes } from '@module-federation/runtime';

// Federation host bootstrap and `shared` registration happen at BUILD time
// via the @module-federation/vite plugin (see vite.config.ts). Here we only
// need to register dynamic remotes so they can be loaded on demand.
//
// Tracks the last-registered manifest entry per remote NAME: registerRemotes
// without `{ force }` no-ops when a remote name is already registered, so on a
// tenant switch (same name, new entry URL) we must force-replace the entry.
const lastEntryByRemoteName = new Map<string, string>();

/* Stands in for "registered, but must be replaced on the next render". Empty is
 * safe because a real entry is always a URL. See `forgetRemoteRegistration`. */
const STALE_ENTRY = '';

export function ensureRemoteRegistered(
  remoteName: string,
  remoteEntry: string
): void {
  if (lastEntryByRemoteName.get(remoteName) === remoteEntry) {
    return;
  }
  registerRemotes([{ name: remoteName, entry: remoteEntry }], {
    force: lastEntryByRemoteName.has(remoteName),
  });
  lastEntryByRemoteName.set(remoteName, remoteEntry);
}

/**
 * Forgets a remote's registration so the next render registers it again with
 * `{ force: true }`.
 *
 * Remounting the `React.lazy` under a failed remote is NOT enough to retry it.
 * `@module-federation/runtime` memoises the load per module, so a remote whose
 * entry failed to fetch keeps handing back the same REJECTED promise, and
 * `ensureRemoteRegistered` above then no-ops because the name and entry are
 * unchanged. Together those mean a "try again" affordance would replay the
 * original failure for the rest of the session, even once the remote came back
 * up — which is exactly what it must not do. Forcing re-registration replaces
 * the remote and drops that cached rejection.
 *
 * Used by retry affordances (see `CustomSectionRenderer`); the normal load path
 * never needs it.
 */
export function forgetRemoteRegistration(remoteName: string): void {
  /* Marked stale rather than deleted, deliberately. `ensureRemoteRegistered`
   * derives `force` from whether the name is already KNOWN, so deleting the
   * entry would re-register without `force` and replace nothing — leaving the
   * rejected promise in place, which is the whole bug. `STALE_ENTRY` can never
   * equal a real entry URL, so the next call takes the force path. */
  lastEntryByRemoteName.set(remoteName, STALE_ENTRY);
}
